<script setup lang="ts">
import { Ticket } from '@/lib/schema/v1/ticket'
import { ExternalLinkIcon, ReaderIcon } from '@radix-icons/vue'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { ScrollArea } from './ui/scroll-area'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip'
import { useUserQuery } from '@/queries/user'
import { queryKeys } from '@/lib/query-keys/v1'
import { usePaginatedTicketsQuery } from '@/queries/v2/ticket'
import { Tickets } from '@/lib/schema/v2/ticket'
import { TicketRouteName } from '@/modules/ticket/routes'
import { ticketParamKey } from '@/composables/route'

const { data: user } = useUserQuery()
const router = useRouter()
const open = ref(false)

const { data: retrieveTicketsResponse } = usePaginatedTicketsQuery(() => ({
    filter: [
        {
            column: 'status_name',
            operator: '!=',
            value: 'closed',
        },
        {
            column: 'assignee_uuid',
            operator: '=',
            value: user.value?.uuid,
        },
    ],
    sort: [
        {
            column: 'priority_number',
            direction: 'asc',
        },
        {
            column: 'status_priority',
            direction: 'asc',
        },
        {
            column: 'updated_at',
            direction: 'desc',
        },
    ],
    fields: 'uuid,title,created_at,creator{uuid,full_name}',
    limit: 99999,
    queryKeys: queryKeys.tickets.key({ filter: 'my-tickets' }),
}))

const tickets = computed<Tickets | null | undefined>(() => {
    return retrieveTicketsResponse.value?.data
})

function goToTicket(ticket: Ticket) {
    router.push({
        name: TicketRouteName.Details,
        params: {
            [ticketParamKey]: ticket.uuid,
        },
    })
}

function openInNewTab(ticket: Ticket) {
    window.open(
        router.resolve({
            name: TicketRouteName.Details,
            params: {
                [ticketParamKey]: ticket.uuid,
            },
        }).href,
        '_blank'
    )
}
</script>

<template>
    <DropdownMenu v-model:open="open">
        <DropdownMenuTrigger as-child>
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger as-child>
                        <Button
                            variant="icon"
                            size="icon"
                            :class="{ 'bg-accent': open, relative: true }"
                            @click="open = !open"
                        >
                            <ReaderIcon />
                            <Badge
                                v-if="tickets && tickets?.length > 0"
                                class="absolute -top-1 left-5 rounded-full px-2 py-1 flex items-center justify-center text-xs bg-black text-white"
                                variant="secondary"
                                >{{ tickets?.length }}</Badge
                            >
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                        <p>My tickets</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </DropdownMenuTrigger>
        <DropdownMenuContent class="w-[500px]" align="end">
            <DropdownMenuLabel class="flex text-xs">My tickets</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <template v-if="tickets && tickets?.length > 0">
                <ScrollArea class="h-[calc(100vh-152px)]">
                    <DropdownMenuItem
                        v-for="ticket in tickets"
                        :key="ticket.uuid"
                        @click="goToTicket(ticket)"
                    >
                        <div class="flex flex-1 space-x-2 text-xs">
                            <Avatar class="h-8 w-8">
                                <AvatarImage
                                    :src="ticket.creator?.profile_photo_url ?? ''"
                                    :alt="ticket.creator?.full_name"
                                />
                                <AvatarFallback>AP</AvatarFallback>
                            </Avatar>
                            <div class="flex-1">
                                <div class="flex items-center justify-between mb-1">
                                    <div class="truncate font-semibold">
                                        {{ ticket.creator?.full_name }}
                                    </div>
                                    <div class="flex items-center shrink-0 text-gray-500 space-x-2">
                                        <div>{{ ticket.created_at.format('llll') }}</div>
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger as-child>
                                                    <Button
                                                        variant="icon"
                                                        size="icon-sm"
                                                        @click.stop="openInNewTab(ticket)"
                                                    >
                                                        <ExternalLinkIcon class="w-4 h-4" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>Open in new tab</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                </div>
                                <div>{{ ticket.title }}</div>
                            </div>
                        </div>
                    </DropdownMenuItem>
                </ScrollArea>
            </template>
            <DropdownMenuItem v-else disabled class="text-xs"
                >No new tickets at the moment.</DropdownMenuItem
            >
        </DropdownMenuContent>
    </DropdownMenu>
</template>
